import {
  AttachmentPrimitive,
  ComposerPrimitive,
  ThreadPrimitive,
  useAssistantRuntime,
  useAui,
  useAuiState,
  useThread,
  useThreadRuntime,
} from "@assistant-ui/react";
import {
  AlertCircle,
  ArrowUpIcon,
  Eye,
  Loader2,
  Paperclip,
  Plus,
  Sparkles,
  Square,
  Upload,
  X,
} from "lucide-react";
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { queuePreMadeResponse } from "../adapters/chatModelAdapter";
import { artifactStore } from "../adapters/artifactStore";
import { isDocumentArtifactGenerating } from "../types/artifact";
import { attachmentErrorStore } from "../adapters/attachmentErrorStore";
import { backgroundGenerationStore } from "../adapters/backgroundGenerationStore";
import { localToRemoteIdMap } from "../adapters/threadListAdapter";
import { CuadraErrorSurfaceContext } from "../contexts/CuadraErrorSurfaceContext";
import { type CuadraChatStrings, DEFAULT_STRINGS } from "../types/strings";

if (typeof window !== "undefined" && import.meta.env?.DEV) {
  (window as never as Record<string, unknown>).__bgStore =
    backgroundGenerationStore;
}
import {
  AssistantMessage,
  ChatErrorIndicator,
  ChatHeader,
  ModelSelector,
  StringsContext,
  UserMessage,
  WelcomeScreen,
} from "./thread";
import type { ModelOption } from "./thread/types";
export type { ModelOption } from "./thread/types";
import "@assistant-ui/react-markdown/styles/dot.css";

declare global {
  interface Window {
    __cuadraThreadListRuntime?: {
      threadList?: { switchToNewThread?: () => Promise<void> };
    } | null;
  }
}

/** Match `min-h-[44px]` on the composer textarea and `maxHeight: 350` inline style. */
const COMPOSER_TEXTAREA_MIN_PX = 44;
const COMPOSER_TEXTAREA_MAX_PX = 350;

/**
 * Stable component identity for `ComposerPrimitive.Attachments` — an inline
 * render function would remount on every parent re-render (blob URL flicker,
 * layout jumps next to the textarea).
 */
const ComposerAttachmentPreview = memo(function ComposerAttachmentPreview() {
  const attachment = useAuiState(({ attachment }) => attachment) as
    | {
        file?: File;
        contentType?: string;
        name?: string;
      }
    | null
    | undefined;

  const imageUrl = useMemo(() => {
    if (
      attachment?.file &&
      attachment.contentType?.startsWith("image/")
    ) {
      return URL.createObjectURL(attachment.file);
    }
    return null;
  }, [attachment?.file, attachment?.contentType]);

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  if (!attachment) return null;

  if (imageUrl) {
    return (
      <div className="cuadra-relative cuadra-inline-block cuadra-mb-2 cuadra-group">
        <img
          src={imageUrl}
          alt={attachment.name || "Image attachment"}
          className="cuadra-rounded-lg cuadra-max-h-24 cuadra-object-cover"
          style={{ border: "1px solid rgba(0,0,0,0.08)" }}
        />
        <AttachmentPrimitive.Remove asChild>
          <button
            type="button"
            className="cuadra-absolute cuadra-top-1 cuadra-right-1 cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-5 cuadra-rounded-full cuadra-bg-black/50 hover:cuadra-bg-black/70 cuadra-transition-colors"
            aria-label="Remove attachment"
          >
            <X className="cuadra-h-3 cuadra-w-3 cuadra-text-white" />
          </button>
        </AttachmentPrimitive.Remove>
      </div>
    );
  }

  return (
    <div
      className="cuadra-relative cuadra-inline-flex cuadra-items-center cuadra-gap-2 cuadra-mb-2 cuadra-px-3 cuadra-py-1.5 cuadra-rounded-lg cuadra-transition-colors"
      style={{
        backgroundColor: "var(--cuadra-muted)",
        border: "1px solid var(--cuadra-border-subtle)",
      }}
    >
      <Paperclip
        className="cuadra-h-3.5 cuadra-w-3.5 cuadra-text-muted-foreground"
        strokeWidth={1.5}
      />
      <span className="cuadra-text-xs cuadra-text-foreground cuadra-font-brand">
        {attachment.name || "Attachment"}
      </span>
      <AttachmentPrimitive.Remove asChild>
        <button
          type="button"
          className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-4 cuadra-rounded-full hover:cuadra-bg-background/60 cuadra-transition-colors cuadra-ml-1"
          aria-label="Remove attachment"
        >
          <X className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground" />
        </button>
      </AttachmentPrimitive.Remove>
    </div>
  );
});

/**
 * Handle for controlling SimpleThread externally
 */
export interface SimpleThreadHandle {
  /** Send a user message programmatically (triggers normal API call) */
  sendMessage: (message: string) => void;
  /** Send a pre-made Q&A pair (instant, simulated streaming, no API call) */
  sendPreMadeQA: (
    question: string,
    answer: string,
    options?: { delay?: number; streamingSpeed?: number },
  ) => void;
  /** Clear the current chat and start fresh */
  clearChat: () => void;
  /** Move keyboard focus to the composer input */
  focusInput: () => void;
}

/** Suggestion with optional pre-made response */
export interface Suggestion {
  /** Suggestion prompt text */
  prompt: string;
  /** Pre-made response (optional) - if provided, response appears instantly with simulated streaming */
  response?: string;
  /** Delay before showing response in ms (overrides global preMadeResponseDelay) */
  responseDelay?: number;
}

export interface SimpleThreadProps {
  /** Welcome screen title */
  welcomeTitle?: string;
  /** Welcome screen subtitle */
  welcomeSubtitle?: string;
  /** Extra top padding for thread viewport (e.g., '1rem', '2rem') */
  extraTopPadding?: string;
  /** Suggestions to show in welcome screen */
  suggestions?: Suggestion[];
  /** Placeholder text for the input field */
  inputPlaceholder?: string;
  /** Enable file attachments in the chat input */
  enableAttachments?: boolean;
  /** Default delay before pre-made responses in ms (default: 1000) */
  preMadeResponseDelay?: number;
  /** Speed of simulated streaming in ms per word (default: 50) */
  streamingSpeed?: number;
  /** Apply safe area insets for mobile devices */
  safeArea?: boolean;

  // --- Claude-like features ---

  /** Show a visible border on the composer (default: false — uses shadow only) */
  composerBorder?: boolean;
  /** Available models for the composer model selector */
  models?: ModelOption[];
  /** Currently selected model ID */
  selectedModelId?: string;
  /** Callback when user selects a different model */
  onModelChange?: (modelId: string) => void;
  /** Show the chat title header at the top of the thread area */
  showChatHeader?: boolean;
  /** Show the theme toggle in the chat header */
  showThemeToggle?: boolean;
  /** Initial theme preference */
  theme?: "light" | "dark" | "system";
  /** URL for the "Upgrade now" link shown on insufficient-credits errors */
  upgradeUrl?: string;
  /** Resolved i18n strings (merged with defaults by WidgetContent) */
  strings?: CuadraChatStrings;
  /** Thread ID being loaded — used to suppress the centered welcome layout
   *  during the brief runtime remount when switching between existing chats. */
  initialThreadId?: string;
  /** Credit/overage state from the billing system for proactive banners */
  creditState?: import("../types/props").CuadraCreditState;
}

/**
 * Simple Thread component wrapper
 * Provides a basic chat interface using assistant-ui primitives
 */
export const SimpleThread = forwardRef<SimpleThreadHandle, SimpleThreadProps>(
  function SimpleThread(
    {
      welcomeTitle = "What should we work on?",
      welcomeSubtitle,
      extraTopPadding,
      suggestions,
      inputPlaceholder,
      enableAttachments = false,
      preMadeResponseDelay = 1000,
      streamingSpeed = 50,
      safeArea = false,
      composerBorder = false,
      models,
      selectedModelId,
      onModelChange,
      showChatHeader = false,
      upgradeUrl,
      strings: s = DEFAULT_STRINGS,
      initialThreadId,
      creditState,
    },
    ref,
  ) {
    const thread = useThreadRuntime();
    const aui = useAui();
    const errorSurfaceId = useContext(CuadraErrorSurfaceContext);
    const composerInputRef = useRef<HTMLTextAreaElement>(null);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
    const [isProcessingPreMade, _setIsProcessingPreMade] = useState(false);

    const attachmentErrorRaw = useSyncExternalStore(
      attachmentErrorStore.subscribe,
      attachmentErrorStore.getSnapshot,
    );
    // Singleton store: require an exact surface match — never broadcast errors with a
    // missing/legacy surface id to every mounted chat (that caused duplicate credit toasts).
    const attachmentError =
      attachmentErrorRaw &&
      attachmentErrorRaw.surfaceId === errorSurfaceId
        ? attachmentErrorRaw
        : null;

    const bgGenerationRaw = useSyncExternalStore(
      backgroundGenerationStore.subscribe,
      backgroundGenerationStore.getSnapshot,
    );

    const threadIsEmpty = useThread((state) => {
      const messages = (state as unknown as { messages?: readonly unknown[] })
        .messages;
      return !messages || messages.length === 0;
    });
    const runtimeThreadId = useThread(
      (state) => (state as unknown as { threadId?: string }).threadId ?? "",
    );

    // Scope the background-generation toast to the current thread.
    // The store is a singleton; without this check every SimpleThread
    // instance would show the toast regardless of which chat is active.
    const resolvedChatId =
      localToRemoteIdMap.get(runtimeThreadId) || runtimeThreadId;
    const bgGeneration =
      bgGenerationRaw && bgGenerationRaw.chatId === resolvedChatId
        ? bgGenerationRaw
        : null;

    const docGenPending = useSyncExternalStore(
      (listener) => artifactStore.subscribe(listener),
      () =>
        isDocumentArtifactGenerating(
          artifactStore.getSnapshot().artifactUiPhase,
        ),
      () => false,
    );

    const composerAttachmentsComponents = useMemo(
      () => ({ Attachment: ComposerAttachmentPreview }),
      [],
    );

    const isRunning = useThread(
      (state) => !!(state as unknown as { isRunning?: boolean }).isRunning,
    );

    // Track composer text so we can reset the textarea height whenever the
    // composer is cleared programmatically (send, cancel, thread switch, etc.).
    // `onInput` on the textarea only fires for user input, not for controlled
    // value updates driven by ComposerPrimitive — so relying on it alone would
    // leave the composer stuck at its expanded height after a send.
    const composerText = useAuiState(
      (s) =>
        (s as unknown as { composer?: { text?: string } }).composer?.text ?? "",
    );

    // Only show the centered welcome layout for brand-new chats.
    // Existing chats being switched to are temporarily empty while history loads —
    // showing the welcome layout for them causes the input to jump to the centre
    // and then snap back down once messages arrive.  When `initialThreadId` is
    // set we know an existing thread is being loaded, so skip the centered layout
    // even before the runtime has assigned a real thread ID.
    const isNewChat =
      (!runtimeThreadId || runtimeThreadId.startsWith("__LOCALID_")) &&
      !initialThreadId;
    const showCenteredWelcome =
      threadIsEmpty && !isLoadingMessages && isNewChat;

    const assistantRuntime = useAssistantRuntime();
    const assistantRuntimeRef = useRef(assistantRuntime);
    assistantRuntimeRef.current = assistantRuntime;
    const prevModelIdRef = useRef(selectedModelId);
    useEffect(() => {
      const prev = prevModelIdRef.current;
      prevModelIdRef.current = selectedModelId;

      if (prev && selectedModelId && prev !== selectedModelId) {
        const timer = setTimeout(() => {
          try {
            void assistantRuntimeRef.current.switchToNewThread();
          } catch (_error) {
            // Silently handle
          }
        }, 50);
        return () => clearTimeout(timer);
      }
    }, [selectedModelId]);

    const sendPreMadeQAInternal = useCallback(
      (
        question: string,
        answer: string,
        options?: { delay?: number; streamingSpeed?: number },
      ) => {
        if (isProcessingPreMade) return;

        try {
          const speed = options?.streamingSpeed ?? streamingSpeed;
          const delay = options?.delay ?? preMadeResponseDelay;
          queuePreMadeResponse(question, answer, speed, delay);

          aui.composer().setText(question);
          setTimeout(() => {
            aui.composer().send();
          }, 10);
        } catch (_err) {
          // Silently handle
        }
      },
      [isProcessingPreMade, streamingSpeed, preMadeResponseDelay, aui],
    );

    const sendMessageInternal = useCallback(
      (message: string) => {
        try {
          aui.composer().setText(message);
          setTimeout(() => {
            aui.composer().send();
          }, 10);
        } catch {
          // Silently handle
        }
      },
      [aui],
    );

    const handleSuggestionClick = useCallback(
      async (suggestion: Suggestion) => {
        if (isProcessingPreMade || !thread) return;

        try {
          if (suggestion.response) {
            const delay = suggestion.responseDelay ?? preMadeResponseDelay;
            await sendPreMadeQAInternal(
              suggestion.prompt,
              suggestion.response,
              {
                delay,
                streamingSpeed,
              },
            );
          } else {
            sendMessageInternal(suggestion.prompt);
          }
        } catch {
          // Silently handle
        }
      },
      [
        thread,
        isProcessingPreMade,
        preMadeResponseDelay,
        streamingSpeed,
        sendPreMadeQAInternal,
        sendMessageInternal,
      ],
    );

    const clearChatInternal = useCallback(async () => {
      try {
        const runtime = window.__cuadraThreadListRuntime;
        const threadListRuntime = runtime?.threadList;

        if (threadListRuntime?.switchToNewThread) {
          await threadListRuntime.switchToNewThread();
        }
      } catch {
        // Silently handle
      }
    }, []);

    const focusInputInternal = useCallback(() => {
      composerInputRef.current?.focus();
    }, []);

    const autoResizeTextarea = useCallback(() => {
      const el = composerInputRef.current;
      if (!el) return;
      // Avoid `height: auto` — it collapses to one line before measuring and
      // causes visible flashing (especially with attachment preview above).
      el.style.height = `${COMPOSER_TEXTAREA_MIN_PX}px`;
      const natural = el.scrollHeight;
      const next = Math.min(
        Math.max(natural, COMPOSER_TEXTAREA_MIN_PX),
        COMPOSER_TEXTAREA_MAX_PX,
      );
      el.style.height = `${next}px`;
      el.style.overflowY =
        natural > COMPOSER_TEXTAREA_MAX_PX ? "auto" : "hidden";
    }, []);

    // Reset textarea height whenever the composer text is cleared
    // programmatically. `onInput` only fires for user input, so without this
    // the expanded height would persist after send, cancel, or thread switch.
    useEffect(() => {
      if (!composerText) autoResizeTextarea();
    }, [composerText, autoResizeTextarea]);

    useImperativeHandle(
      ref,
      () => ({
        sendMessage: sendMessageInternal,
        sendPreMadeQA: sendPreMadeQAInternal,
        clearChat: clearChatInternal,
        focusInput: focusInputInternal,
      }),
      [
        sendMessageInternal,
        sendPreMadeQAInternal,
        clearChatInternal,
        focusInputInternal,
      ],
    );

    useEffect(() => {
      if (!thread) {
        setIsLoadingMessages(false);
        return;
      }

      try {
        interface RuntimeStore {
          getState?: () => {
            threadId?: string;
            messages?: unknown[];
            isLoadingHistory?: boolean;
            isRunning?: boolean;
          };
          subscribe?: (
            callback: (state: {
              threadId?: string;
              messages?: unknown[];
              isLoadingHistory?: boolean;
              isRunning?: boolean;
            }) => void,
          ) => () => void;
        }
        const runtimeStore = (thread as { store?: RuntimeStore })?.store;
        if (!runtimeStore) {
          setIsLoadingMessages(false);
          return;
        }

        const unsubscribe = runtimeStore.subscribe?.((state) => {
          const threadId = state?.threadId;
          const messages = (state?.messages || []) as Array<{
            role?: string;
            content?: Array<{ type?: string; text?: string }> | string;
          }>;
          const isLoadingHistory = state?.isLoadingHistory || false;
          const messageCount = messages.length;

          if (threadId && threadId !== currentThreadId) {
            setCurrentThreadId(threadId);
          }

          const shouldShowLoading = isLoadingHistory && messageCount === 0;
          setIsLoadingMessages(shouldShowLoading);
        });

        const state = runtimeStore.getState?.();
        if (state) {
          const threadId = state?.threadId;
          const messages = (state?.messages || []) as Array<{
            role?: string;
            content?: Array<{ type?: string; text?: string }> | string;
          }>;
          const isLoadingHistory = state?.isLoadingHistory || false;
          const messageCount = messages.length;

          if (threadId && threadId !== currentThreadId) {
            setCurrentThreadId(threadId);
          }

          const shouldShowLoading = isLoadingHistory && messageCount === 0;
          setIsLoadingMessages(shouldShowLoading);
        }

        return () => {
          if (unsubscribe) {
            unsubscribe();
          }
        };
      } catch {
        setIsLoadingMessages(false);
      }
    }, [thread, currentThreadId]);

    return (
      <StringsContext.Provider value={s}>
        <ThreadPrimitive.Root className="cuadra-bg-inherit cuadra-flex cuadra-flex-col cuadra-w-full cuadra-h-full cuadra-relative">
          {/* Top bar — centered model selector + optional thread selector */}
          {(showChatHeader || (models && models.length > 0)) && (
            <div className="cuadra-relative cuadra-flex cuadra-items-center cuadra-justify-center cuadra-px-3 cuadra-py-2 cuadra-min-h-[48px]">
              {showChatHeader && (
                <div className="cuadra-absolute cuadra-right-3 md:cuadra-right-auto md:cuadra-left-3 cuadra-top-0 cuadra-bottom-0 cuadra-flex cuadra-items-center">
                  <ChatHeader />
                </div>
              )}
              {models && models.length > 0 && (
                <ModelSelector
                  models={models}
                  selectedModelId={selectedModelId}
                  onModelChange={onModelChange}
                  lockedLabel={s.modelLocked}
                />
              )}
            </div>
          )}
          {/* Drag-and-drop + content wrapper */}
          <ComposerPrimitive.AttachmentDropzone
            disabled={!enableAttachments}
            className={`cuadra-flex-1 cuadra-flex cuadra-flex-col cuadra-min-h-0 cuadra-relative ${showCenteredWelcome ? "cuadra-justify-center" : ""}`}
          >
            {/* Drag overlay */}
            <div
              className="cuadra-dropzone-overlay cuadra-pointer-events-none cuadra-absolute cuadra-inset-0 cuadra-z-50 cuadra-flex cuadra-items-center cuadra-justify-center cuadra-rounded-lg cuadra-transition-opacity cuadra-duration-200"
              style={{ opacity: 0 }}
            >
              <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-gap-3">
                <div
                  className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-14 cuadra-rounded-2xl"
                  style={{
                    backgroundColor: "var(--cuadra-primary)",
                    color: "white",
                  }}
                >
                  <Upload className="cuadra-h-6 cuadra-w-6" />
                </div>
                <p className="cuadra-text-sm cuadra-font-medium cuadra-text-foreground cuadra-font-brand">
                  {s.dropFilesHere}
                </p>
              </div>
            </div>

            <ThreadPrimitive.Viewport
              className={`cuadra-w-full cuadra-bg-inherit cuadra-relative cuadra-scrollbar-thin ${
                showCenteredWelcome
                  ? "cuadra-flex-none"
                  : "cuadra-flex-1 cuadra-min-h-0 cuadra-overflow-y-auto"
              }`}
              role="log"
              aria-live="polite"
              aria-label="Chat messages"
              style={{
                paddingTop: showCenteredWelcome
                  ? "0"
                  : extraTopPadding ||
                    (showChatHeader || (models && models.length > 0)
                      ? "0.5rem"
                      : "2rem"),
              }}
            >
              {isLoadingMessages ? (
                <div
                  className="cuadra-absolute cuadra-inset-0 cuadra-flex cuadra-items-center cuadra-justify-center"
                  aria-busy="true"
                >
                  <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-gap-3">
                    <Loader2
                      className="cuadra-h-5 cuadra-w-5 cuadra-animate-spin cuadra-text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="cuadra-text-sm cuadra-text-muted-foreground cuadra-font-brand">
                      {s.loadingMessages}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="cuadra-max-w-3xl cuadra-mx-auto cuadra-w-full cuadra-px-4">
                  <ThreadPrimitive.Empty>
                    {welcomeTitle ? (
                      <WelcomeScreen
                        title={welcomeTitle}
                        subtitle={welcomeSubtitle}
                      />
                    ) : null}
                  </ThreadPrimitive.Empty>
                  <ThreadPrimitive.Messages
                    components={{
                      UserMessage: UserMessage,
                      AssistantMessage: AssistantMessage,
                    }}
                  />
                  <ChatErrorIndicator />
                </div>
              )}
            </ThreadPrimitive.Viewport>

            {/* Composer area */}
            <div
              className={`cuadra-w-full cuadra-px-4 cuadra-pt-2 ${showCenteredWelcome ? "cuadra-pb-2" : "cuadra-pb-4"}`}
              style={
                safeArea
                  ? {
                      paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
                    }
                  : undefined
              }
            >
              <div className="cuadra-max-w-3xl cuadra-mx-auto cuadra-w-full">
                {/* Proactive credit warning banner (from creditState prop) */}
                {!attachmentError && creditState && (() => {
                  const isOverage = creditState.isInOverage && !creditState.hardCap;
                  const isLow = !isOverage && creditState.available != null && creditState.limit != null
                    && creditState.limit > 0 && creditState.available / creditState.limit < 0.1
                    && creditState.available > 0;
                  if (!isOverage && !isLow) return null;

                  return (
                    <div
                      role="status"
                      className="cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-mb-2 cuadra-px-3.5 cuadra-py-2 cuadra-rounded-xl cuadra-text-sm cuadra-font-brand cuadra-animate-in cuadra-fade-in cuadra-slide-in-from-bottom-2"
                      style={{
                        backgroundColor: "var(--cuadra-toast-warning-bg)",
                        border: "1px solid var(--cuadra-toast-warning-border)",
                      }}
                    >
                      <AlertCircle
                        className="cuadra-h-4 cuadra-w-4 cuadra-flex-shrink-0"
                        style={{ color: "var(--cuadra-toast-warning-icon)" }}
                      />
                      <span style={{ color: "var(--cuadra-toast-warning-text)" }}>
                        {isOverage ? s.overageWarning : s.lowCredits}
                      </span>
                    </div>
                  );
                })()}
                {/* Error toast */}
                {attachmentError &&
                  (() => {
                    const isCredits =
                      attachmentError.code === "INSUFFICIENT_CREDITS";
                    const isOverageError =
                      attachmentError.code === "CREDIT_OVERAGE";
                    const isPremium =
                      attachmentError.code === "PREMIUM_MODEL_UPGRADE";
                    const isDailyLimit =
                      attachmentError.code === "DAILY_LIMIT_EXHAUSTED";
                    const isVision =
                      attachmentError.code === "VISION_NOT_SUPPORTED";
                    const isService = attachmentError.code === "SERVICE_ERROR";
                    const isValidation = [
                      "FILE_TOO_LARGE",
                      "INVALID_TYPE",
                    ].includes(attachmentError.code);
                    const showUpgrade = isCredits || isPremium;

                    const palette = showUpgrade || isDailyLimit
                      ? {
                          bg: "var(--cuadra-toast-upgrade-bg)",
                          border: "var(--cuadra-toast-upgrade-border)",
                          text: "var(--cuadra-toast-upgrade-text)",
                          icon: "var(--cuadra-primary, #F4633A)",
                          dismiss: "var(--cuadra-toast-upgrade-dismiss)",
                        }
                      : isOverageError
                        ? {
                            bg: "var(--cuadra-toast-warning-bg)",
                            border: "var(--cuadra-toast-warning-border)",
                            text: "var(--cuadra-toast-warning-text)",
                            icon: "var(--cuadra-toast-warning-icon)",
                            dismiss: "var(--cuadra-toast-warning-dismiss)",
                          }
                        : isVision || isValidation
                          ? {
                              bg: "var(--cuadra-toast-info-bg)",
                              border: "var(--cuadra-toast-info-border)",
                              text: "var(--cuadra-toast-info-text)",
                              icon: "var(--cuadra-toast-info-icon)",
                              dismiss: "var(--cuadra-toast-info-dismiss)",
                            }
                          : {
                              bg: "var(--cuadra-toast-warning-bg)",
                              border: "var(--cuadra-toast-warning-border)",
                              text: "var(--cuadra-toast-warning-text)",
                              icon: "var(--cuadra-toast-warning-icon)",
                              dismiss: "var(--cuadra-toast-warning-dismiss)",
                            };

                    const IconComponent = (showUpgrade || isDailyLimit)
                      ? Sparkles
                      : isVision
                        ? Eye
                        : AlertCircle;

                    const displayMessage = isDailyLimit
                      ? s.dailyLimitExhausted
                      : isOverageError
                        ? s.overageWarning
                        : isCredits
                          ? s.creditsError
                          : isPremium
                            ? s.premiumModelError
                            : isService
                              ? s.serviceError
                              : attachmentError.message;

                    return (
                      <div
                        role="alert"
                        aria-live="assertive"
                        className="cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-mb-2 cuadra-px-3.5 cuadra-py-2 cuadra-rounded-xl cuadra-text-sm cuadra-font-brand cuadra-animate-in cuadra-fade-in cuadra-slide-in-from-bottom-2"
                        style={{
                          backgroundColor: palette.bg,
                          border: `1px solid ${palette.border}`,
                        }}
                      >
                        <IconComponent
                          className="cuadra-h-4 cuadra-w-4 cuadra-flex-shrink-0"
                          style={{ color: palette.icon }}
                        />
                        <div className="cuadra-flex-1 cuadra-flex cuadra-flex-wrap cuadra-items-center cuadra-gap-2">
                          <span style={{ color: palette.text }}>
                            {displayMessage}
                          </span>
                          {(showUpgrade || isDailyLimit) && upgradeUrl && (
                            <a
                              href={upgradeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cuadra-inline-flex cuadra-items-center cuadra-justify-center cuadra-gap-1.5 cuadra-whitespace-nowrap cuadra-rounded-md cuadra-px-3 cuadra-py-1.5 cuadra-text-xs cuadra-font-medium cuadra-text-white cuadra-shadow-sm cuadra-transition-colors hover:cuadra-opacity-90"
                              style={{
                                backgroundColor:
                                  "var(--cuadra-primary, #F4633A)",
                              }}
                            >
                              {s.upgrade}
                              <ArrowUpIcon className="cuadra-h-3 cuadra-w-3" />
                            </a>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            attachmentErrorStore.clearIfSurface(errorSurfaceId)
                          }
                          className="cuadra-flex-shrink-0 cuadra-p-0.5 cuadra-rounded cuadra-transition-colors hover:cuadra-bg-black/5"
                          aria-label="Dismiss"
                        >
                          <X
                            className="cuadra-h-3.5 cuadra-w-3.5"
                            style={{ color: palette.dismiss }}
                          />
                        </button>
                      </div>
                    );
                  })()}
                {/* Document generation + background recovery (same strip; doc copy takes precedence while pending) */}
                {(docGenPending || bgGeneration) &&
                  (() => {
                    const isPolling =
                      docGenPending || bgGeneration?.status === "polling";
                    const isCompleted =
                      !docGenPending && bgGeneration?.status === "completed";
                    const isError =
                      !docGenPending &&
                      (bgGeneration?.status === "error" ||
                        bgGeneration?.status === "timeout");

                    const palette = isError
                      ? {
                          bg: "var(--cuadra-toast-warning-bg)",
                          border: "var(--cuadra-toast-warning-border)",
                          text: "var(--cuadra-toast-warning-text)",
                          icon: "var(--cuadra-toast-warning-icon)",
                          dismiss: "var(--cuadra-toast-warning-dismiss)",
                        }
                      : {
                          bg: "var(--cuadra-toast-info-bg)",
                          border: "var(--cuadra-toast-info-border)",
                          text: "var(--cuadra-toast-info-text)",
                          icon: "var(--cuadra-toast-info-icon)",
                          dismiss: "var(--cuadra-toast-info-dismiss)",
                        };

                    const message = isPolling
                      ? docGenPending
                        ? s.generatingDocument
                        : s.backgroundGenerating
                      : isCompleted
                        ? s.backgroundComplete
                        : bgGeneration?.status === "timeout"
                          ? s.backgroundTimeout
                          : s.backgroundError;

                    return (
                      <div
                        role="status"
                        aria-live="polite"
                        className="cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-mb-2 cuadra-px-3.5 cuadra-py-2 cuadra-rounded-xl cuadra-text-sm cuadra-font-brand cuadra-animate-in cuadra-fade-in cuadra-slide-in-from-bottom-2"
                        style={{
                          backgroundColor: palette.bg,
                          border: `1px solid ${palette.border}`,
                        }}
                      >
                        {isPolling ? (
                          <Loader2
                            className="cuadra-h-4 cuadra-w-4 cuadra-flex-shrink-0 cuadra-animate-spin"
                            style={{ color: palette.icon }}
                          />
                        ) : (
                          <AlertCircle
                            className="cuadra-h-4 cuadra-w-4 cuadra-flex-shrink-0"
                            style={{ color: palette.icon }}
                          />
                        )}
                        <span
                          className="cuadra-flex-1"
                          style={{ color: palette.text }}
                        >
                          {message}
                        </span>
                        {!isPolling && bgGeneration && (
                          <button
                            type="button"
                            onClick={() => backgroundGenerationStore.clear()}
                            className="cuadra-flex-shrink-0 cuadra-p-0.5 cuadra-rounded cuadra-transition-colors hover:cuadra-bg-black/5"
                            aria-label="Dismiss"
                          >
                            <X
                              className="cuadra-h-3.5 cuadra-w-3.5"
                              style={{ color: palette.dismiss }}
                            />
                          </button>
                        )}
                      </div>
                    );
                  })()}
                {/* Composer attachments preview */}
                {enableAttachments && (
                  <ComposerPrimitive.Attachments
                    components={composerAttachmentsComponents}
                  />
                )}
                <ComposerPrimitive.Root
                  className="cuadra-relative cuadra-w-full cuadra-rounded-2xl cuadra-flex cuadra-flex-col"
                  style={{
                    backgroundColor: "var(--cuadra-composer)",
                    boxShadow: "var(--cuadra-composer-shadow)",
                    border: composerBorder
                      ? "1px solid hsl(var(--cuadra-border))"
                      : "none",
                  }}
                >
                  <ComposerPrimitive.Input asChild>
                    <textarea
                      ref={composerInputRef}
                      rows={1}
                      onInput={autoResizeTextarea}
                      aria-label="Message input"
                      placeholder={inputPlaceholder ?? s.inputPlaceholder}
                      disabled={docGenPending}
                      title={docGenPending ? s.generatingDocument : undefined}
                      className="cuadra-flex cuadra-min-h-[44px] cuadra-w-full cuadra-rounded-t-2xl cuadra-border-0 cuadra-bg-transparent cuadra-px-4 cuadra-pt-3.5 cuadra-pb-1 cuadra-text-base cuadra-text-foreground placeholder:cuadra-text-muted-foreground focus-visible:cuadra-outline-none disabled:cuadra-cursor-not-allowed disabled:cuadra-opacity-50 cuadra-resize-none cuadra-font-brand"
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.5",
                        maxHeight: `${COMPOSER_TEXTAREA_MAX_PX}px`,
                        overflowY: "hidden",
                      }}
                    />
                  </ComposerPrimitive.Input>
                  <div className="cuadra-flex cuadra-items-center cuadra-justify-between cuadra-px-3 cuadra-pb-2.5 cuadra-pt-1">
                    <div className="cuadra-flex cuadra-items-center cuadra-gap-1">
                      {enableAttachments && (
                        <ComposerPrimitive.AddAttachment multiple asChild>
                          <button
                            type="button"
                            disabled={docGenPending}
                            className="cuadra-group cuadra-relative cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-8 cuadra-rounded-lg hover:cuadra-bg-muted/60 cuadra-transition-colors disabled:cuadra-pointer-events-none disabled:cuadra-opacity-40"
                            aria-label="Attach files"
                          >
                            <Plus
                              className="cuadra-h-4 cuadra-w-4 cuadra-text-muted-foreground"
                              strokeWidth={1.5}
                            />
                            <span
                              className="cuadra-pointer-events-none cuadra-absolute cuadra-bottom-full cuadra-left-1/2 cuadra--translate-x-1/2 cuadra-mb-1.5 cuadra-whitespace-nowrap cuadra-rounded-md cuadra-px-2.5 cuadra-py-1 cuadra-text-xs cuadra-text-foreground cuadra-opacity-0 group-hover:cuadra-opacity-100 cuadra-transition-opacity cuadra-duration-150 cuadra-delay-300 cuadra-font-brand"
                              style={{
                                backgroundColor: "var(--cuadra-popover)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              }}
                            >
                              {s.attachFiles}
                            </span>
                          </button>
                        </ComposerPrimitive.AddAttachment>
                      )}
                    </div>
                    <div className="cuadra-flex cuadra-items-center cuadra-gap-2">
                      {isRunning ? (
                        <ComposerPrimitive.Cancel asChild>
                          <button
                            className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-8 cuadra-rounded-full cuadra-transition-all cuadra-duration-200 hover:cuadra-opacity-90"
                            style={{
                              backgroundColor: "var(--cuadra-primary)",
                              color: "white",
                            }}
                            aria-label="Stop generating"
                            type="button"
                          >
                            <Square
                              className="cuadra-h-3.5 cuadra-w-3.5"
                              fill="currentColor"
                              strokeWidth={0}
                            />
                          </button>
                        </ComposerPrimitive.Cancel>
                      ) : (
                        <ComposerPrimitive.Send asChild>
                          <button
                            disabled={docGenPending}
                            className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-8 cuadra-rounded-full cuadra-transition-all cuadra-duration-200 hover:cuadra-opacity-90 disabled:cuadra-opacity-30 disabled:cuadra-cursor-not-allowed"
                            style={{
                              backgroundColor: "var(--cuadra-primary)",
                              color: "white",
                            }}
                            aria-label="Send message"
                            type="button"
                          >
                            <ArrowUpIcon
                              className="cuadra-h-4 cuadra-w-4"
                              strokeWidth={2}
                            />
                          </button>
                        </ComposerPrimitive.Send>
                      )}
                    </div>
                  </div>
                </ComposerPrimitive.Root>
                <p className="cuadra-text-[14px] cuadra-text-muted-foreground cuadra-text-center cuadra-mt-1.5 cuadra-font-brand">
                  {s.aiDisclaimer}
                </p>
              </div>
            </div>

            {/* Suggestion pills */}
            {showCenteredWelcome && suggestions && suggestions.length > 0 && (
              <div className="cuadra-flex cuadra-flex-wrap cuadra-justify-center cuadra-gap-2 cuadra-px-4 cuadra-pb-6 cuadra-pt-1 cuadra-max-w-3xl cuadra-mx-auto cuadra-w-full">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`suggestion-${index}`}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isProcessingPreMade}
                    className="cuadra-px-3.5 cuadra-py-2 cuadra-rounded-full cuadra-text-sm cuadra-text-muted-foreground hover:cuadra-text-foreground cuadra-transition-all cuadra-duration-200 cuadra-font-brand cuadra-cursor-pointer disabled:cuadra-opacity-50 disabled:cuadra-cursor-not-allowed"
                    style={{
                      backgroundColor: "var(--cuadra-composer)",
                      boxShadow: "0 0 0 1px var(--cuadra-border-subtle)",
                    }}
                  >
                    {suggestion.prompt}
                  </button>
                ))}
              </div>
            )}
          </ComposerPrimitive.AttachmentDropzone>
        </ThreadPrimitive.Root>
      </StringsContext.Provider>
    );
  },
);
